import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchUserDetailsReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { ReportApiDateRangeQuery, UserDetailsReportApiItem } from '../../types/report';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function userDetailsReportRowId(row: UserDetailsReportApiItem) {
  return String(row.user_id);
}

export function UserDetailsReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 90 },
    { field: 'first_name_en', headerName: 'First name (EN)', minWidth: 120, flex: 0.2 },
    { field: 'family_name_en', headerName: 'Family name (EN)', minWidth: 120, flex: 0.2 },
    { field: 'first_name_ar', headerName: 'First name (AR)', minWidth: 120, flex: 0.2 },
    { field: 'family_name_ar', headerName: 'Family name (AR)', minWidth: 120, flex: 0.2 },
    { field: 'national_id', headerName: 'National ID', width: 130 },
    { field: 'email', headerName: 'Email', minWidth: 220, flex: 0.35 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    { field: 'status', headerName: 'Status', width: 110 },
    { field: 'register_date', headerName: 'Register date', type: 'dateTime', width: 180 },
    { field: 'investment_status', headerName: 'Investment status', width: 150 },
    { field: 'address_id', headerName: 'Address Id', type: 'number', width: 100 },
    { field: 'address_street_name', headerName: 'Street', minWidth: 100, flex: 0.15 },
    { field: 'address_city', headerName: 'City', width: 110 },
    { field: 'address_line_1', headerName: 'Line 1', minWidth: 140, flex: 0.2 },
    { field: 'address_line_2', headerName: 'Line 2', minWidth: 140, flex: 0.2 },
    { field: 'address_additional_number', headerName: 'Add. no.', width: 100 },
    { field: 'address_district', headerName: 'District', width: 110 },
    { field: 'address_unit_number', headerName: 'Unit', width: 90 },
    { field: 'address_is_primary', headerName: 'Primary', type: 'number', width: 90 },
    { field: 'address_building_number', headerName: 'Building', width: 100 },
    { field: 'address_post_code', headerName: 'Post code', width: 100 },
    { field: 'address_location_coordinates', headerName: 'Coordinates', minWidth: 120, flex: 0.15 },
    { field: 'full_name_en', headerName: 'Full name (EN)', minWidth: 160, flex: 0.2, sortable: false },
    { field: 'full_name_ar', headerName: 'Full name (AR)', minWidth: 160, flex: 0.2, sortable: false },
    { field: 'kyc_email', headerName: 'KYC email', minWidth: 200, flex: 0.3, sortable: false },
    { field: 'kyc_phone', headerName: 'KYC phone', width: 150, sortable: false },
    { field: 'marital_status', headerName: 'Marital status', width: 130, sortable: false },
    { field: 'id_expiry_date', headerName: 'ID expiry date', width: 170, sortable: false },
    { field: 'iqama_expiry_date', headerName: 'Iqama expiry date', width: 170, sortable: false },
    { field: 'id_type', headerName: 'ID type', width: 110, sortable: false },
    { field: 'national_address', headerName: 'National address', minWidth: 240, flex: 0.35, sortable: false },
    { field: 'nafath_address', headerName: 'Nafath address', minWidth: 240, flex: 0.35, sortable: false },
  ]);

  const { userDetailsReportItems, totalUserDetailsReportItems, fetchUserDetailsReportStatus, error } =
    useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'user-details-report.csv',
    useCallback(() => reportService.exportUserDetailsReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">User details</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchUserDetailsReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={userDetailsReportItems}
          columns={columns.current}
          getRowId={userDetailsReportRowId}
          loading={fetchUserDetailsReportStatus === fetchStatuses.pending}
          rowCount={totalUserDetailsReportItems}
          initialSortModel={[{ field: 'register_date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
